/**
 * Deterministic node placement for `MindspaceGraph`'s `layout='fixed'` seam.
 *
 * A test seam, not a feature — the same contract `GridGlyph404.freezeAt` and
 * `CardSwap`'s resting placement document. The product always runs the force
 * simulation; this exists so a story can know where a node ended up.
 *
 * ## Why a graph needs one at all
 *
 * `MindspaceGraph` paints to a canvas, so there is no DOM node to query: the
 * only way a play function can click a concept is to work out its pixel
 * position and click there. Under the force simulation that position is the
 * output of a few hundred physics ticks seeded by the library's own jitter, so
 * it is different on every run and unknowable from outside. Pinning `fx`/`fy`
 * replaces that with arithmetic, which is what turns §2.10 #27 and #28 from
 * "renders without throwing" into real assertions — and gives the component
 * its first stable Chromatic baseline, which a physics layout can never have.
 *
 * ## Why a ring
 *
 * Every node is placed on one circle, which makes two things true that a grid
 * or a random scatter do not. Adjacent-node spacing is a single number the
 * radius can be solved for, so "no two discs overlap" is guaranteed rather
 * than eyeballed. And every node sits at a distinct angle from the centre, so
 * the links between them fan across the middle instead of running along a
 * lattice — which keeps the hover-highlight chain legible in a snapshot.
 *
 * It is emphatically NOT what the product looks like, and it should not be
 * mistaken for it. The force layout clusters; this one deliberately does not.
 * A story showing "what the archive looks like" wants the force layout; a
 * story asserting what a click does wants this one.
 */

/**
 * Minimum centre-to-centre distance between two adjacent nodes.
 *
 * Twice `MindspaceGraph`'s `NODE_COLLIDE_RADIUS`, which is the separation the
 * real `forceCollide` guarantees — so the frozen layout is no denser than one
 * the simulation would have been allowed to settle into.
 */
export const MIN_NODE_SEPARATION = 44;

/**
 * Smallest ring the layout will draw, in graph units.
 *
 * Below about four nodes the separation maths alone yields a ring tighter than
 * the label pills, which are drawn BELOW each node and would then overlap the
 * node opposite. A floor is cheaper than modelling label extents.
 */
export const MIN_RING_RADIUS = 80;

/**
 * Radius of the ring `count` nodes sit on.
 *
 * Solved from the chord length: adjacent nodes subtend `2π/count`, so their
 * separation is `2·R·sin(π/count)`. Setting that to `MIN_NODE_SEPARATION` and
 * inverting gives the smallest non-overlapping ring, then the floor applies.
 *
 * `count <= 1` has no adjacent pair — `sin(π/1)` is zero and the division
 * would be infinite — so a lone node sits at the origin on a zero radius.
 */
export function fixedRingRadius(count: number): number {
  if (count <= 1) return 0;
  const solved = MIN_NODE_SEPARATION / (2 * Math.sin(Math.PI / count));
  return Math.max(MIN_RING_RADIUS, solved);
}

/**
 * Where node `index` of `count` sits, in graph coordinates.
 *
 * The origin is the centre of the ring, which is also where `MindspaceGraph`
 * pins the viewport in fixed mode — so a story converts to pixels with
 * `canvasCentre + position`, at zoom 1, with no library maths involved.
 *
 * Index 0 is at twelve o'clock and the ring runs clockwise, matching how a
 * reader scans a circular arrangement. That only matters because it makes the
 * expected position of a named node something you can work out by eye when a
 * story fails.
 */
export function fixedNodePosition(
  index: number,
  count: number
): { x: number; y: number } {
  const radius = fixedRingRadius(count);
  // -π/2 puts index 0 at the top; positive angles then run clockwise because
  // canvas y grows downward.
  const angle = -Math.PI / 2 + (2 * Math.PI * index) / Math.max(1, count);
  return { x: radius * Math.cos(angle), y: radius * Math.sin(angle) };
}
